import {apiService} from '../apiService';
import {FORM_ACCESS_LEVELS} from '../../constants/formAccessLevels';

export const formsService = apiService.injectEndpoints({
    endpoints: (builder) => {
        return {
            getForm: builder.query({
                query: (formId) => ({
                    url: `/forms/${formId}`
                }),
                transformResponse: (response) => {
                    const {schema, ...rest} = response;

                    return {schema: JSON.parse(schema), ...rest};
                },
            }),
            getForms: builder.query({
                query: (body) => ({
                    url: '/forms/list',
                    method: 'post',
                    data: body
                }),
                transformErrorResponse: (_) => ({message: 'Failed to retrieve Forms'}),
                providesTags: (_) => [{type: 'Forms', id: 'LIST'}]
            }),
            getAssignmentForms: builder.query({
                query: ({formId, queryParams}) => ({
                    url: `/forms/${formId}/assignment-forms`,
                    method: 'get',
                    params: queryParams
                }),
                transformErrorResponse: (_) => ({message: 'Failed to retrieve private Forms'}),
                providesTags: (_) => ['PrivateForms']
            }),
            getPublicForm: builder.query({
                query: ({id, type, queryParams}) =>
                    ({
                        url: type === FORM_ACCESS_LEVELS.publicWithStudentLink
                            ? `/forms/requests/student/${id}`
                            : `/forms/public/${id}`,
                        params: queryParams
                    }),
                transformResponse: (response) => {
                    const {
                        id,
                        name,
                        schema,
                        data,
                        status,
                        schoolName,
                        studentForename,
                        hisHer,
                        heShe,
                        himHer,
                        studentFullName,
                        placeholders,
                        parentFullName,
                        parentSurname,
                        employeeFullName,
                        title,
                        isCommunication,
                    } = response;
                    return {
                        formId: id,
                        formName: name,
                        schema: schema ? JSON.parse(schema) : {},
                        data: data ? (typeof data === 'string' ? JSON.parse(data) : data) : {},
                        status,
                        placeholderValues: placeholders
                            ? JSON.parse(placeholders)
                            : {
                                schoolName,
                                studentForename,
                                studentFullName,
                                hisHer,
                                heShe,
                                himHer,
                                title: title ?? '',
                                parent: parentFullName,
                                parentSurname,
                                employee: employeeFullName,
                            },
                        isCommunication,
                    };
                },
                transformErrorResponse: (error) => {
                    const errorMessages = {
                        500: 'Internal server error.',
                        404: 'Could not find the requested form.',
                        410: 'Form was not found or already submitted.'
                    };

                    return {
                        statusText: error.status,
                        message: errorMessages[error.status] ?? 'Something went wrong.'
                    };
                }
            }),
            updateFormStatus: builder.mutation({
                query: (body) => ({
                    url: '/forms/status',
                    method: 'put',
                    data: body,
                }),
                invalidatesTags: [{type: 'Forms', id: 'LIST'}],
            }),
            archiveForm: builder.mutation({
                query: (formId) => ({
                    url: `/forms/archive/${formId}`,
                    method: 'patch'
                }),
                invalidatesTags: [{type: 'Forms', id: 'LIST'}],
            }),
            restoreForm: builder.mutation({
                query: (formId) => ({
                    url: `/forms/restore/${formId}`,
                    method: 'patch'
                }),
                invalidatesTags: [{
                    type: 'Forms',
                    id: 'LIST'
                }],
            }),
            deleteForm: builder.mutation({
                query: (formId) => ({
                    url: `/forms/delete/${formId}`,
                    method: 'delete'
                }),
                invalidatesTags: [{type: 'Forms', id: 'LIST'}],
            }),
            cloneForm: builder.mutation({
                query: (formId) => ({
                    url: `/forms/clone/${formId}`,
                    method: 'POST'
                }),
                invalidatesTags: [{type: 'Forms', id: 'LIST'}],
            }),
            getFormTemplates: builder.query({
                query: () => ({
                    url: '/forms/templates'
                }),
                transformErrorResponse: (_) => ({message: 'Failed to retrieve Forms'}),
                transformResponse: (response) => (
                    response.templates.map(template => ({
                        id: template.id,
                        name: template.name,
                        schema: JSON.parse(template.schema),
                        type: template.type,
                        category: template.category,
                        parentCategory: template.parentCategory,
                        schoolId: template.schoolId,
                    }))
                )
            }),
            createFormTemplate: builder.mutation({
                query: (body) => ({
                    url: '/forms/templates',
                    method: 'POST',
                    data: body,
                }),
                transformErrorResponse: (error) => ({
                    message: (typeof error.data === 'string' ? error.data : error.data?.message) ?? 'Failed to create template.',
                }),
            }),
            getSchoolTemplates: builder.query({
                query: (body) => ({
                    url: '/forms/templates/school',
                    method: 'POST',
                    data: body
                }),
                transformErrorResponse: (_) => ({message: 'Failed to retrieve School Templates'}),
                transformResponse: (response) => response,
                providesTags: (_) => [{type: 'SchoolTemplates', id: 'LIST'}]
            }),
            updateSchoolTemplate: builder.mutation({
                query: ({templateId, ...body}) => ({
                    url: `/forms/templates/${templateId}`,
                    method: 'PUT',
                    data: body,
                }),
                invalidatesTags: [{type: 'SchoolTemplates', id: 'LIST'}],
            }),
            deleteSchoolTemplate: builder.mutation({
                query: (templateId) => ({
                    url: `/forms/templates/${templateId}`,
                    method: 'DELETE'
                }),
                invalidatesTags: [{type: 'SchoolTemplates', id: 'LIST'}],
            }),
            createForm: builder.mutation({
                query: ({schema, ...rest}) => ({
                    url: '/forms',
                    method: 'POST',
                    data: {
                        schema: JSON.stringify(schema),
                        ...rest
                    },
                }),
            }),
            updateForm: builder.mutation({
                query: ({schema, ...rest}) => ({
                    url: '/forms/details',
                    method: 'PUT',
                    data: {
                        schema: JSON.stringify(schema),
                        ...rest
                    },
                }),
            }),
            submitEnquiryForm: builder.mutation({
                query: (body) => ({
                    url: '/forms/enquiry',
                    method: 'POST',
                    data: body
                }),
            }),
            getEnquiryResponses: builder.query({
                query: (body) => ({
                    url: '/forms/enquiries',
                    method: 'POST',
                    data: body
                }),
                transformErrorResponse: (_) => ({message: 'Failed to Enquiry responses'}),
                providesTags: (_) => [{type: 'EnquiryResponses', id: 'LIST'}]
            }),
            deleteEnquirySubmissions: builder.mutation({
                query: (body) => ({
                    url: '/forms/enquiries',
                    method: 'DELETE',
                    data: body
                }),
                invalidatesTags: [{type: 'EnquiryResponses', id: 'LIST'}],
            }),
            getNonExistingSchools: builder.query({
                query: () => ({
                    url: '/student/enquiry/non-existing-schools'
                }),
                providesTags: ['NonExistingSchools']
            }),
            getFormAccess: builder.query({
                query: (formId) => ({
                    url: `/forms/${formId}/access`
                })
            }),
            manageFormAccess: builder.mutation({
                query: (body) => ({
                    method: 'PUT',
                    url: '/forms/manage-access',
                    data: body
                }),
                invalidatesTags: (result, error, {formId}) =>
                    error ? [] : [{type: 'FormAccess', id: formId}]
            }),
        };
    },
});

export const {
    useGetFormQuery,
    useLazyGetFormQuery,
    useGetFormsQuery,
    useGetAssignmentFormsQuery,
    useGetPublicFormQuery,
    useUpdateFormStatusMutation,
    useArchiveFormMutation,
    useRestoreFormMutation,
    useDeleteFormMutation,
    useCloneFormMutation,
    useGetFormTemplatesQuery,
    useCreateFormTemplateMutation,
    useGetSchoolTemplatesQuery,
    useUpdateSchoolTemplateMutation,
    useDeleteSchoolTemplateMutation,
    useCreateFormMutation,
    useUpdateFormMutation,
    useSubmitEnquiryFormMutation,
    useGetEnquiryResponsesQuery,
    useDeleteEnquirySubmissionsMutation,
    useGetNonExistingSchoolsQuery,
    useGetFormAccessQuery,
    useManageFormAccessMutation
} = formsService;
